/**
 * One fetch per snapshot PART, not one per row (LOCKER-374)
 * ========================================================
 *
 * A snapshot part holds every pair in its id range — thousands of rows in a 6 MB
 * object. Opening three rows that all live in part 3 was fetching part 3 three
 * times, which defeats the point of splitting by id range in the first place: the
 * range exists so one row costs ONE part, and a part already paid for should cost
 * nothing.
 *
 * Two distinct dedupes, and both are needed:
 *
 *  - **Already fetched** — served straight from the parsed part.
 *  - **Currently fetching** — a second row opened before the first fetch resolves
 *    joins that same promise. Without this, a click-click-click on three rows of
 *    one part still fires three requests, because nothing is in the cache yet.
 *
 * Bounded on purpose. A parsed 6 MB part is tens of megabytes of live objects, so
 * an operator walking a 64 MB run would otherwise hold the whole thing in the tab.
 * Least-recently-used eviction keeps the working set to the parts actually being
 * read.
 */

/** Rows as a snapshots part stores them; the pair shape is the caller's business. */
export type SnapshotPartRow = { sstId: string }

export type SnapshotPartCache<T extends SnapshotPartRow> = {
  /** The part's rows, fetching it at most once however many rows ask for it. */
  rowsFor: (key: string) => Promise<T[]>
  /** True once the part is resolved and held — a row read from it costs no request. */
  isLoaded: (key: string) => boolean
  /** Requests actually issued. The dedupe is only provable by counting them. */
  fetchCount: () => number
  loadedKeys: () => string[]
}

export const DEFAULT_MAX_CACHED_PARTS = 3

export const createSnapshotPartCache = <T extends SnapshotPartRow>(
  fetchPart: (key: string) => Promise<T[]>,
  opts?: { maxParts?: number },
): SnapshotPartCache<T> => {
  const maxParts = opts?.maxParts ?? DEFAULT_MAX_CACHED_PARTS
  if (maxParts < 1) throw new Error("maxParts must be at least 1")
  // Insertion order IS the LRU order: a hit re-inserts, so the oldest key is
  // always the first one Map iteration yields.
  const parts = new Map<string, T[]>()
  const inFlight = new Map<string, Promise<T[]>>()
  let fetches = 0

  const touch = (key: string, rows: T[]) => {
    parts.delete(key)
    parts.set(key, rows)
    while (parts.size > maxParts) {
      const oldest = parts.keys().next().value
      if (oldest === undefined) break
      parts.delete(oldest)
    }
  }

  return {
    rowsFor: async (key) => {
      const held = parts.get(key)
      if (held) {
        touch(key, held)
        return held
      }
      const pending = inFlight.get(key)
      if (pending) return pending
      fetches += 1
      const promise = fetchPart(key)
        .then((rows) => {
          inFlight.delete(key)
          touch(key, rows)
          return rows
        })
        .catch((error) => {
          // Dropped, not cached: a failed fetch must be retryable, and caching the
          // rejection would make one transient 500 permanent for the whole session.
          inFlight.delete(key)
          throw error
        })
      inFlight.set(key, promise)
      return promise
    },
    isLoaded: (key) => parts.has(key),
    fetchCount: () => fetches,
    loadedKeys: () => [...parts.keys()],
  }
}
